import { useDrag } from "@use-gesture/react";
import classNames from "classnames";
import { IObservableValue, untracked } from "mobx";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef } from "react";
import {
  ActivePaneState,
  ConstructedPane,
  ConstructedTemplate,
  PaneOutput,
  PaneType,
  Position,
  Rect,
} from "../Types";
import { FeatherIcon } from "../ui/FeatherIcon";
import { canvasViewport } from "./CanvasState";
import { GeneratedCode } from "./GeneratedCode";
import { Wire } from "./PaneInput";
import { PaneRenderOutputComponent } from "./PaneRenderOutput";
import {
  calculateNewZoomCanvasTransform,
  scalePosition,
  TEMPLATE_CANVAS_CLASSNAME,
} from "./SandboxUtils";

const emptyFunction = () => undefined;

const TemplateCanvasPane = observer(
  ({
    pane,
    isMain,
    activePaneState,
    rect,
    outputBoxes,
  }: {
    pane: ConstructedPane;
    isMain: boolean;
    activePaneState: ActivePaneState;
    rect: Rect;
    outputBoxes: IObservableValue<PaneOutput>[];
  }) => {
    const output = outputBoxes[0].get();
    return (
      <div
        className={classNames(
          "absolute bg-neutral-0 rounded border overflow-hidden flex flex-col",
          isMain ? "border-neutral-400" : "border-neutral-250"
        )}
        style={{
          transform: `translate(${rect[0]}px, ${rect[1]}px)`,
          width: `${rect[2]}px`,
          height: `${rect[3]}px`,
        }}
      >
        <div className="font-mono-natto flex-shrink-0 h-8 px-2 flex items-center text-neutral-400 bg-neutral-50 border-b border-neutral-150">
          {pane.type === PaneType.Evaluate
            ? pane.name ?? "eval"
            : pane.type === PaneType.State
            ? "state"
            : pane.type === PaneType.Import
            ? ` ${pane.module[0]} ${pane.module[1]}`
            : null}
        </div>
        {pane.type === PaneType.Evaluate ? (
          <div className="flex-shrink-0 bg-neutral-50 border-b border-neutral-150 max-h-[50%] overflow-auto">
            <GeneratedCode expression={pane.expression} />
          </div>
        ) : null}
        <div className="grow overflow-auto">
          {output[0] === "value" ? (
            <PaneRenderOutputComponent
              paneId="template"
              value={output[1]}
              renderOutput={
                pane.type === PaneType.Evaluate ? pane.renderOutput : undefined
              }
              disableDragExtract={true}
              getRenderInput={emptyFunction}
            />
          ) : null}
        </div>
      </div>
    );
  }
);

const TEMPLATE_CANVAS_PADDING = 32;
export const PaneEvaluateTemplateComponentCanvas = observer(
  ({
    activePaneState,
    constructedTemplate,
    constructedOutputBoxes,
  }: {
    activePaneState: ActivePaneState;
    constructedTemplate: ConstructedTemplate;
    constructedOutputBoxes: IObservableValue<PaneOutput>[][];
  }) => {
    const paneRect = activePaneState.rect.get();
    const rootRef = useRef<HTMLDivElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const offsetRef = useRef<Position>([0, 0]);
    const scaleRef = useRef(1);
    useEffect(() => {
      const paneRects = constructedTemplate.panes.map(
        (p) => p.rect ?? [0, 0, paneRect[2], paneRect[3]]
      );
      let minX =
        Math.min(...paneRects.map((r) => r[0])) - TEMPLATE_CANVAS_PADDING;
      let minY =
        Math.min(...paneRects.map((r) => r[1])) - TEMPLATE_CANVAS_PADDING;
      let maxX =
        Math.max(...paneRects.map((r) => r[0] + r[2])) +
        TEMPLATE_CANVAS_PADDING;
      let maxY =
        Math.max(...paneRects.map((r) => r[1] + r[3])) +
        TEMPLATE_CANVAS_PADDING;
      const rootElement = rootRef.current!;
      const rootWidth = rootElement.offsetWidth;
      const rootHeight = rootElement.offsetHeight;
      const xScale = rootWidth / (maxX - minX);
      const yScale = rootHeight / (maxY - minY);
      const scale = Math.min(xScale, yScale);
      let xOffset = -minX;
      let yOffset = -minY;
      if (scale === xScale) {
        yOffset += (rootHeight / scale - (maxY - minY)) / 2;
      } else {
        xOffset += (rootWidth / scale - (maxX - minX)) / 2;
      }
      const offset: Position = [xOffset * scale, yOffset * scale];
      offsetRef.current = offset;
      scaleRef.current = scale;
      containerRef.current!.style.transform = `translate(${offset[0]}px, ${offset[1]}px) scale(${scale})`;
    }, []);

    const bindDrag = useDrag(
      ({ target, first, last, offset }) => {
        offsetRef.current = offset;
        containerRef.current!.style.transform = `translate(${offset[0]}px, ${offset[1]}px) scale(${scaleRef.current})`;
        if (first && target instanceof HTMLElement) {
          target.style.cursor = "grabbing";
        }
        if (last && target instanceof HTMLElement) {
          target.style.cursor = "";
        }
      },
      {
        from: () => offsetRef.current,
        transform: (v) =>
          untracked(() => scalePosition(v, 1 / canvasViewport.zoom)),
        pointer: {
          buttons: [1, 4],
        },
        filterTaps: true,
      }
    );
    const mainPaneRect: Rect = [0, 0, paneRect[2], paneRect[3]];

    return (
      <div
        className={classNames(
          "bg-neutral-100 relative h-full overflow-hidden",
          TEMPLATE_CANVAS_CLASSNAME
        )}
        ref={rootRef}
      >
        <div
          {...bindDrag()}
          onDoubleClick={(e) => {
            const prevZoom = scaleRef.current;
            scaleRef.current = prevZoom * 1.5;
            const rootRect = rootRef.current!.getBoundingClientRect();
            offsetRef.current = calculateNewZoomCanvasTransform(
              offsetRef.current,
              scalePosition(
                [e.clientX - rootRect.x, e.clientY - rootRect.y],
                1 / canvasViewport.zoom
              ),
              prevZoom,
              scaleRef.current
            );
            containerRef.current!.style.transform = `translate(${offsetRef.current[0]}px, ${offsetRef.current[1]}px) scale(${scaleRef.current})`;
          }}
          className="absolute top-0 left-0 right-0 bottom-0 touch-none"
        />
        <div className="origin-top-left" ref={containerRef}>
          {constructedTemplate.panes.map((pane, paneIndex) => (
            <React.Fragment key={paneIndex}>
              {pane.type === PaneType.Evaluate
                ? pane.inputs.map((input, inputIndex) => {
                    if (typeof input.source === "string") {
                      return null;
                    }
                    const fromPaneRect =
                      constructedTemplate.panes[input.source[0]].rect ??
                      mainPaneRect;
                    const toPaneRect = pane.rect ?? mainPaneRect;
                    return (
                      <Wire
                        from={[
                          fromPaneRect[0] + fromPaneRect[2],
                          fromPaneRect[1] + 8 + 16 * (input.source[1] ?? 0),
                        ]}
                        opacityClassName="text-opacity-20"
                        to={[
                          toPaneRect[0],
                          8 + toPaneRect[1] + inputIndex * 16,
                        ]}
                        key={inputIndex}
                      />
                    );
                  })
                : null}
              <TemplateCanvasPane
                pane={pane}
                activePaneState={activePaneState}
                isMain={paneIndex === 0}
                rect={pane.rect ?? mainPaneRect}
                outputBoxes={constructedOutputBoxes[paneIndex]}
              />
            </React.Fragment>
          ))}
        </div>
        <div className="absolute bottom-2 left-2 flex border border-neutral-200 select-none rounded-sm overflow-hidden">
          <button
            onClick={() => {
              const prevZoom = scaleRef.current;
              scaleRef.current = prevZoom * 1.3;
              offsetRef.current = calculateNewZoomCanvasTransform(
                offsetRef.current,
                [
                  rootRef.current!.offsetWidth / 2,
                  rootRef.current!.offsetHeight / 2,
                ],
                prevZoom,
                scaleRef.current
              );
              containerRef.current!.style.transform = `translate(${offsetRef.current[0]}px, ${offsetRef.current[1]}px) scale(${scaleRef.current})`;
            }}
            className="bg-neutral-0 w-6 h-6 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition"
          >
            <FeatherIcon icon="zoom-in" size="sm" />
          </button>
          <div className="w-px bg-neutral-200" />
          <button
            onClick={() => {
              const prevZoom = scaleRef.current;
              scaleRef.current = prevZoom / 1.3;
              offsetRef.current = calculateNewZoomCanvasTransform(
                offsetRef.current,
                [
                  rootRef.current!.offsetWidth / 2,
                  rootRef.current!.offsetHeight / 2,
                ],
                prevZoom,
                scaleRef.current
              );
              containerRef.current!.style.transform = `translate(${offsetRef.current[0]}px, ${offsetRef.current[1]}px) scale(${scaleRef.current})`;
            }}
            className="bg-neutral-0 w-6 h-6 flex items-center justify-center text-neutral-400 hover:text-neutral-500 transition"
          >
            <FeatherIcon icon="zoom-out" size="sm" />
          </button>
        </div>
      </div>
    );
  }
);
